import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { Box, useTheme } from "@mui/material";
import { useState, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  PropertyPriceFormFields,
  propertyPriceSchema,
} from "@/modules/property-tracks/utils";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { PaymentTerms, PropertyPrice } from "@/modules/property-tracks/types";
import { TextButton } from "@/modules/common/components/ui/text-button";
import { PencilLine } from "lucide-react";
import PriceDetailsForm from "../../track-actions/add-track-detailed/price-details/price-details-form";
import { useEditPropertyPrice } from "@/modules/property-tracks/hooks/edit-property-price.hook";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";

const constructPriceType = (
  propertyPrice?: PropertyPrice
): PropertyPriceFormFields | undefined => {
  if (!propertyPrice) return;
  switch (propertyPrice.paymentType) {
    case "cash":
      return {
        // priceRole: propertyPrice.priceRole,
        paymentType: propertyPrice.paymentType ?? undefined,
        price: propertyPrice.price ?? undefined,
      };
    case "installments":
      return {
        // priceRole: propertyPrice.priceRole,
        paymentType: propertyPrice.paymentType ?? undefined,
        price:
          (propertyPrice.downPayment ?? 0) +
          (propertyPrice.remainingInstallments ?? 0),
        remainingInstallments: propertyPrice.remainingInstallments ?? undefined,
        downPayment: propertyPrice.downPayment ?? undefined,
        maintenance: propertyPrice.maintenance ?? undefined,
        clubhouse: propertyPrice.clubhouse ?? undefined,
        parking: propertyPrice.parking ?? undefined,
        durationRemainingMonths:
          propertyPrice.durationRemainingMonths ?? undefined,
        durationRemainingYears:
          propertyPrice.durationRemainingYears ?? undefined,
        installmentFrequency: propertyPrice.installmentFrequency
          ? {
              label: PaymentTerms[propertyPrice.installmentFrequency],
              value: propertyPrice.installmentFrequency,
            }
          : undefined,
      };
  }
};

const EditPriceInfo = ({
  propertyUId,
  trackUid,
  propertyPrice,
}: {
  propertyUId: string;
  trackUid: string;
  propertyPrice: PropertyPrice;
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();

  const defaultValues = constructPriceType(propertyPrice);

  const form = useForm<PropertyPriceFormFields>({
    resolver: zodResolver(propertyPriceSchema),
  });

  const { handleSubmit, reset, watch, setValue } = form;

  const paymentType = watch("paymentType");
  const downPayment = watch("downPayment");
  const remainingInstallments = watch("remainingInstallments");

  // Keep askingPrice in sync with downPayment + remainingInstallments for installments
  useEffect(() => {
    if (paymentType === "installments") {
      const calculatedPrice =
        Number(downPayment ?? 0) + Number(remainingInstallments ?? 0);
      setValue("price", calculatedPrice, { shouldValidate: false });
    }
    if (paymentType === "cash") {
      setValue("price", undefined, { shouldValidate: false });
      setValue("downPayment", undefined, { shouldValidate: false });
      setValue("remainingInstallments", undefined, { shouldValidate: false });
      setValue("maintenance", undefined, { shouldValidate: false });
      setValue("clubhouse", undefined, { shouldValidate: false });
      setValue("parking", undefined, { shouldValidate: false });
    }
  }, [paymentType, downPayment, remainingInstallments, setValue]);

  const onSubmit: SubmitHandler<PropertyPriceFormFields> = async (data) => {
    submitEditPropertyPrice(data);
  };

  const mutationEditProperty = useEditPropertyPrice();

  const submitEditPropertyPrice = (data: PropertyPriceFormFields) => {
    mutationEditProperty.mutate(
      {
        uid: propertyUId,
        data: {
          ...data,
          priceRole: propertyPrice.priceRole,
          installmentFrequency: data.installmentFrequency
            ? data.installmentFrequency.value
            : undefined,
          trackUid: trackUid,
        },
      },
      {
        onSuccess: () => {
          setModalOpen(false);
          reset();
          showToast({
            type: ToastType.SUCCESS,
            message: "Property updated successfully",
            toastId: TOAST_IDS.EDIT_PROPERTY_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.EDIT_PROPERTY_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      }
    );
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    reset();
  };

  const handleModalOpen = () => {
    setModalOpen(true);
    reset(defaultValues);
  };

  const { readonly } = useReadonlyContext();

  return (
    <>
      <TextButton
        disabled={readonly}
        onClick={handleModalOpen}
        sx={{ display: "flex", gap: 0.5, alignItems: "bottom" }}
      >
        <PencilLine size={16} /> Edit
      </TextButton>
      <EditFieldModal
        open={modalOpen}
        setOpen={setModalOpen}
        header="Edit Unit Financials"
        width={480}
        handleConfirm={handleSubmit(onSubmit)}
        handleCloseModal={handleCloseModal}
        isLoading={mutationEditProperty.isPending}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.md,
          }}
        >
          <PriceDetailsForm form={form} />
        </Box>
      </EditFieldModal>
    </>
  );
};

export default EditPriceInfo;
